import React, { useState, useEffect, useMemo } from 'react'
import {
  Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogContent, Grid, IconButton, InputAdornment,
  LinearProgress, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography, alpha,
  Fade, Badge, Divider
} from '@mui/material'
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  HourglassEmpty as PendingIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  NotificationsActive as NotifIcon,
  AssignmentTurnedIn as AssignIcon,
  MonetizationOn as MoneyIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Refresh as RefreshIcon,
  WarningAmber as WarningIcon,
  CheckCircleOutline as CheckIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import Swal from 'sweetalert2'
import {
  loadPettyExpenses,
  approvePettyExpense,
  rejectPettyExpense
} from '../BaseURL'

// ─── Brand colours (match PettyExpenses.jsx) ────────────────────────
var PRIMARY       = '#11998e'
var PRIMARY_DARK  = '#0d7a71'
var PRIMARY_LIGHT = '#1abc9c'
var GHS           = 'GH\u20b5'

// Inner appTheme + ThemeProvider removed — page now inherits the global
// AppThemeProvider so it adapts to both default and black themes.
var _appThemeRemoved = ({
  palette: {
    mode: 'light',
    primary:    { main: PRIMARY,     dark: PRIMARY_DARK,  light: PRIMARY_LIGHT },
    background: { default: '#f0faf8', paper: '#FFFFFF' },
    success:    { main: '#11998e' },
    warning:    { main: '#e6a817' },
    error:      { main: '#e05252' }
  },
  typography: {
    fontFamily: '"Nunito", "Segoe UI", sans-serif',
    h5: { fontWeight: 800, letterSpacing: '-0.5px' },
    h6: { fontWeight: 700 }
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 700, borderRadius: 10, boxShadow: 'none' },
        containedPrimary: {
          background: 'linear-gradient(135deg, #0d7a71 0%, #11998e 60%, #1abc9c 100%)',
          color: '#fff',
          '&:hover': {
            background: 'linear-gradient(135deg, #0a6560 0%, #0d7a71 100%)',
            boxShadow: '0 4px 14px rgba(17,153,142,0.4)'
          }
        }
      }
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10, backgroundColor: 'background.paper',
            '&:hover fieldset': { borderColor: PRIMARY },
            '&.Mui-focused fieldset': { borderColor: PRIMARY_DARK, borderWidth: 2 }
          }
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& th': {
            background: 'linear-gradient(135deg, #0d7a71 0%, #11998e 100%)',
            color: '#fff', fontWeight: 700, fontSize: '0.72rem',
            letterSpacing: '0.8px', textTransform: 'uppercase',
            borderBottom: 'none', padding: '14px 16px'
          }
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: alpha(PRIMARY, 0.04) },
          '&:last-child td': { borderBottom: 'none' }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: { root: { padding: '12px 16px', fontSize: '0.83rem' } }
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 16, backgroundImage: 'none' } }
    }
  }
})

function fmtAmount(val) {
  return parseFloat(val || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 })
}

function isToday(dateStr) {
  var today = new Date().toISOString().substring(0, 10)
  var expDate = (dateStr || '').substring(0, 10)
  return expDate === today
}

function getStatusConfig(status) {
  if (status === 'approved') return { color: 'success', label: 'Approved', icon: <ApproveIcon sx={{ fontSize: 13 }} /> }
  if (status === 'rejected') return { color: 'error',   label: 'Rejected', icon: <RejectIcon  sx={{ fontSize: 13 }} /> }
  return { color: 'warning', label: 'Pending', icon: <PendingIcon sx={{ fontSize: 13 }} /> }
}

// ─── Stat Card ───────────────────────────────────────────────────────
function StatCard({ icon, label, value, color, sub }) {
  return (
    <Card elevation={0} sx={{ border: '1.5px solid', borderColor: alpha(color, 0.22), borderRadius: 3, flex: 1, minWidth: 155 }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2.5, background: alpha(color, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
            {icon}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.8px', fontSize: '0.6rem', fontWeight: 700 }}>
              {label}
            </Typography>
            <Typography sx={{ color, fontWeight: 800, lineHeight: 1.2, fontSize: '1.1rem' }}>
              {value}
            </Typography>
            {sub && <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>{sub}</Typography>}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

// ─── Dialog Header ────────────────────────────────────────────────────
function DialogHeader({ gradient, titleIcon, title, subtitle, onClose }) {
  return (
    <Box sx={{ background: gradient, color: '#fff', borderRadius: '16px 16px 0 0', px: 3, py: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 38, height: 38, borderRadius: 2, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {titleIcon}
          </Box>
          <Box>
            <Typography fontWeight={800} fontSize="1rem" color="#fff">{title}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.7rem' }}>{subtitle}</Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose}
          sx={{ color: '#fff', background: 'rgba(255,255,255,0.12)', '&:hover': { background: 'rgba(255,255,255,0.22)' }, borderRadius: 2 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  )
}

// ─── Notification Toast ───────────────────────────────────────────────
function NotificationToast({ notifications, onDismiss }) {
  if (!notifications.length) return null
  return (
    <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 2000, display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 360 }}>
      {notifications.map(function(n) {
        return (
          <Fade key={n.id} in>
            <Paper elevation={8} sx={{
              p: 2, borderRadius: 3,
              borderLeft: '4px solid',
              borderLeftColor: n.type === 'approved' ? PRIMARY : '#e05252',
              background: 'background.paper',
              display: 'flex', alignItems: 'flex-start', gap: 1.5
            }}>
              <Box sx={{ color: n.type === 'approved' ? PRIMARY : '#e05252', mt: 0.3 }}>
                {n.type === 'approved' ? <ApproveIcon sx={{ fontSize: 20 }} /> : <RejectIcon sx={{ fontSize: 20 }} />}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={700} sx={{ color: n.type === 'approved' ? PRIMARY_DARK : '#c0392b', fontSize: '0.82rem' }}>
                  {n.type === 'approved' ? 'Expense Approved' : 'Expense Rejected'}
                </Typography>
                <Typography variant="caption" color="text.secondary">{n.message}</Typography>
              </Box>
              <IconButton size="small" onClick={function() { onDismiss(n.id) }} sx={{ p: 0.3 }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Paper>
          </Fade>
        )
      })}
    </Box>
  )
}

// ─── Main Component ───────────────────────────────────────────────────
function ExpenseApproval() {

  var s1 = useState([]);          var expenses = s1[0];          var setExpenses = s1[1]
  var s2 = useState(true);        var loading = s2[0];           var setLoading = s2[1]
  var s3 = useState('pending');   var statusFilter = s3[0];      var setStatusFilter = s3[1]
  var s4 = useState('');          var search = s4[0];            var setSearch = s4[1]
  var s5 = useState({ from: '', to: '' }); var dateFilter = s5[0]; var setDateFilter = s5[1]
  var s6 = useState(null);        var viewTarget = s6[0];        var setViewTarget = s6[1]
  var s7 = useState(false);       var actionLoading = s7[0];     var setActionLoading = s7[1]
  var s8 = useState([]);          var notifications = s8[0];     var setNotifications = s8[1]
  var s9 = useState('');          var currentUser = s9[0];       var setCurrentUser = s9[1]

  // Load logged-in user
  useEffect(function() {
    var saved = localStorage.getItem('userInfo')
    if (saved) {
      try { setCurrentUser(JSON.parse(saved).name || '') } catch(e) {}
    }
  }, [])

  // Fetch all expenses
  function fetchExpenses() {
    setLoading(true)
    fetch(loadPettyExpenses)
      .then(function(res) { return res.json() })
      .then(function(data) { setExpenses(data.results || data || []) })
      .catch(function() { setExpenses([]) })
      .finally(function() { setLoading(false) })
  }

  useEffect(function() { fetchExpenses() }, [])

  // Push a toast notification
  function pushNotif(type, message) {
    var id = Date.now()
    setNotifications(function(prev) { return [...prev, { id, type, message }] })
    setTimeout(function() {
      setNotifications(function(prev) { return prev.filter(function(n) { return n.id !== id }) })
    }, 5000)
  }

  function dismissNotif(id) {
    setNotifications(function(prev) { return prev.filter(function(n) { return n.id !== id }) })
  }

  // ── Approve ──────────────────────────────────────────────────────────
  function handleApprove(exp) {
    if (!isToday(exp.expense_date)) {
      Swal.fire({
        icon: 'warning',
        title: 'Not Allowed',
        text: 'Expenses can only be approved on the same day they were submitted.',
        confirmButtonColor: '#11998e',
        confirmButtonText: 'OK'
      })
      return
    }
    Swal.fire({
      title: 'Approve Expense?',
      html:
        '<div style="text-align:left;font-size:0.9rem">' +
        '<b>Paid To:</b> ' + exp.paid_to + '<br/>' +
        '<b>Amount:</b> GH₵ ' + fmtAmount(exp.amount) + '<br/>' +
        '<b>By:</b> ' + exp.created_by +
        '</div>',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: PRIMARY,
      cancelButtonColor: '#aaa',
      confirmButtonText: 'Yes, Approve',
      cancelButtonText: 'Cancel',
      borderRadius: '16px'
    }).then(function(result) {
      if (!result.isConfirmed) return
      setActionLoading(true)
      fetch(approvePettyExpense + '/' + exp.id, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: currentUser })
      })
        .then(function(res) { return res.json() })
        .then(function() {
          fetchExpenses()
          pushNotif('approved', exp.paid_to + ' — ' + GHS + ' ' + fmtAmount(exp.amount) + ' approved for ' + exp.created_by)
          Swal.fire({
            icon: 'success',
            title: 'Approved!',
            text: 'Expense has been approved and the user has been notified.',
            confirmButtonColor: PRIMARY,
            timer: 2000,
            showConfirmButton: false
          })
        })
        .catch(function(err) {
          Swal.fire({ icon: 'error', title: 'Failed', text: err.message || 'Could not approve expense.', confirmButtonColor: PRIMARY })
        })
        .finally(function() { setActionLoading(false) })
    })
  }

  // ── Reject ───────────────────────────────────────────────────────────
  function handleReject(exp) {
    if (!isToday(exp.expense_date)) {
      Swal.fire({
        icon: 'warning',
        title: 'Not Allowed',
        text: 'Expenses can only be rejected on the same day they were submitted.',
        confirmButtonColor: '#11998e',
        confirmButtonText: 'OK'
      })
      return
    }
    Swal.fire({
      title: 'Reject Expense?',
      html:
        '<div style="text-align:left;font-size:0.9rem;margin-bottom:12px">' +
        '<b>Paid To:</b> ' + exp.paid_to + '<br/>' +
        '<b>Amount:</b> GH₵ ' + fmtAmount(exp.amount) +
        '</div>' +
        '<input id="reject-reason" class="swal2-input" placeholder="Reason for rejection (optional)" style="font-size:0.85rem"/>',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e05252',
      cancelButtonColor: '#aaa',
      confirmButtonText: 'Yes, Reject',
      cancelButtonText: 'Cancel',
      preConfirm: function() {
        return document.getElementById('reject-reason').value || ''
      }
    }).then(function(result) {
      if (!result.isConfirmed) return
      var reason = result.value || ''
      setActionLoading(true)
      fetch(rejectPettyExpense + '/' + exp.id, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved_by: currentUser, reason: reason })
      })
        .then(function(res) { return res.json() })
        .then(function() {
          fetchExpenses()
          pushNotif('rejected', exp.paid_to + ' — ' + GHS + ' ' + fmtAmount(exp.amount) + ' rejected for ' + exp.created_by + (reason ? ' (' + reason + ')' : ''))
          Swal.fire({
            icon: 'info',
            title: 'Rejected',
            text: 'Expense has been rejected and the user has been notified.',
            confirmButtonColor: PRIMARY,
            timer: 2000,
            showConfirmButton: false
          })
        })
        .catch(function(err) {
          Swal.fire({ icon: 'error', title: 'Failed', text: err.message || 'Could not reject expense.', confirmButtonColor: PRIMARY })
        })
        .finally(function() { setActionLoading(false) })
    })
  }

  // ── Filter ────────────────────────────────────────────────────────────
  var filtered = useMemo(function() {
    return expenses.filter(function(e) {
      var matchStatus = statusFilter === 'all' || e.status === statusFilter

      var q = search.toLowerCase().trim()
      var matchSearch = !q ||
        (e.paid_to || '').toLowerCase().includes(q) ||
        (e.created_by || '').toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q)

      // Normalize date — MySQL may return "2025-01-15T00:00:00.000Z" or "2025-01-15 00:00:00"
      var expDate = (e.expense_date || '').substring(0, 10)
      var matchFrom = !dateFilter.from || expDate >= dateFilter.from
      var matchTo   = !dateFilter.to   || expDate <= dateFilter.to

      return matchStatus && matchSearch && matchFrom && matchTo
    })
  }, [expenses, statusFilter, search, dateFilter])

  // ── Stats ─────────────────────────────────────────────────────────────
  var pendingList   = expenses.filter(function(e) { return e.status === 'pending' })
  var approvedList  = expenses.filter(function(e) { return e.status === 'approved' })
  var rejectedList  = expenses.filter(function(e) { return e.status === 'rejected' })
  var pendingTotal  = pendingList.reduce(function(s, e)  { return s + parseFloat(e.amount || 0) }, 0)
  var approvedTotal = approvedList.reduce(function(s, e) { return s + parseFloat(e.amount || 0) }, 0)

  return (
    <>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 2, md: 4 } }}>

        {/* ── Header ── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{
              width: 52, height: 52, borderRadius: 3,
              background: 'linear-gradient(135deg, #0d7a71, #11998e)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 18px rgba(17,153,142,0.35)'
            }}>
              <AssignIcon sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ color: PRIMARY_DARK }}>Expense Approval</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.4px' }}>
                Review and action petty cash requests
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* Pending badge */}
            <Badge badgeContent={pendingList.length} color="warning" max={99}>
              <Chip
                icon={<PendingIcon sx={{ fontSize: 15 }} />}
                label="Pending"
                sx={{ fontWeight: 700, background: alpha('#e6a817', 0.12), color: '#b37d0a', borderColor: alpha('#e6a817', 0.4), border: '1.5px solid' }}
              />
            </Badge>
            <Button variant="outlined" startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <RefreshIcon />}
              onClick={fetchExpenses} disabled={loading}
              sx={{ borderRadius: 2.5, borderColor: PRIMARY, color: PRIMARY, fontWeight: 700, '&:hover': { background: alpha(PRIMARY, 0.06) } }}>
              Refresh
            </Button>
          </Stack>
        </Stack>

        {/* ── Stat Cards ── */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
          <StatCard icon={<PendingIcon />}  label="Awaiting Review"   value={pendingList.length}                       color="#e6a817"  sub={GHS + ' ' + fmtAmount(pendingTotal) + ' total'} />
          <StatCard icon={<ApproveIcon />}  label="Approved"          value={approvedList.length}                      color={PRIMARY}  sub={GHS + ' ' + fmtAmount(approvedTotal)} />
          <StatCard icon={<RejectIcon />}   label="Rejected"          value={rejectedList.length}                      color="#e05252"  sub="requests declined" />
          <StatCard icon={<MoneyIcon />}    label="Total Approved"    value={GHS + ' ' + fmtAmount(approvedTotal)}     color={PRIMARY_DARK} />
        </Stack>

        {/* ── Filter Bar ── */}
        <Paper elevation={0} sx={{ p: 2, mb: 2.5, border: '1.5px solid', borderColor: alpha(PRIMARY, 0.15) }}>
          <Grid container spacing={1.5} alignItems="flex-end">

            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.6}
                sx={{ textTransform: 'uppercase', letterSpacing: '0.7px', fontSize: '0.62rem' }}>
                Date From
              </Typography>
              <TextField fullWidth type="date" value={dateFilter.from}
                onChange={function(e) { setDateFilter(function(p) { return Object.assign({}, p, { from: e.target.value }) }) }} />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.6}
                sx={{ textTransform: 'uppercase', letterSpacing: '0.7px', fontSize: '0.62rem' }}>
                Date To
              </Typography>
              <TextField fullWidth type="date" value={dateFilter.to}
                onChange={function(e) { setDateFilter(function(p) { return Object.assign({}, p, { to: e.target.value }) }) }} />
            </Grid>

            <Grid item xs={12} sm={6} md={2.5}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.6}
                sx={{ textTransform: 'uppercase', letterSpacing: '0.7px', fontSize: '0.62rem' }}>
                Search
              </Typography>
              <TextField fullWidth placeholder="Paid to, staff name, description..." value={search}
                onChange={function(e) { setSearch(e.target.value) }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment> }} />
            </Grid>

            <Grid item xs={12} sm={6} md={2.5}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={0.6}
                sx={{ textTransform: 'uppercase', letterSpacing: '0.7px', fontSize: '0.62rem' }}>
                Status
              </Typography>
              <TextField fullWidth select value={statusFilter} onChange={function(e) { setStatusFilter(e.target.value) }}>
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>
            </Grid>

          </Grid>
        </Paper>

        {/* ── Table ── */}
        <Paper elevation={0} sx={{ overflow: 'hidden', border: '1.5px solid', borderColor: alpha(PRIMARY, 0.15) }}>
          {(loading || actionLoading) && (
            <LinearProgress sx={{ height: 3, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #11998e, #38ef7d)' } }} />
          )}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Date', 'Paid To', 'Amount', 'Description', 'Created By', 'Receipt', 'Status', 'Actions'].map(function(h) {
                    return <TableCell key={h}>{h}</TableCell>
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <Stack alignItems="center" spacing={1.5}>
                        <CircularProgress size={32} sx={{ color: PRIMARY }} />
                        <Typography variant="caption" color="text.disabled">Loading expenses...</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Stack alignItems="center" spacing={1.5}>
                        <ReceiptIcon sx={{ fontSize: 48, color: 'grey.300' }} />
                        <Typography color="text.disabled" fontWeight={700}>No expenses found</Typography>
                        <Typography variant="caption" color="text.disabled">Adjust filters or refresh</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : filtered.map(function(exp, i) {
                  var cfg = getStatusConfig(exp.status)
                  var isPending = exp.status === 'pending'
                  return (
                    <TableRow key={exp.id || i}
                      sx={{ background: isPending ? alpha('#e6a817', 0.035) : 'inherit' }}>

                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.78rem' }}>
                        {exp.expense_date}
                      </TableCell>

                      <TableCell sx={{ fontWeight: 700 }}>{exp.paid_to}</TableCell>

                      <TableCell>
                        <Typography fontWeight={800} sx={{ color: PRIMARY_DARK, fontFamily: '"Courier New", monospace', fontSize: '0.9rem' }}>
                          {GHS} {fmtAmount(exp.amount)}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ maxWidth: 180 }}>
                        <Tooltip title={exp.description || ''} placement="top">
                          <Typography variant="body2" noWrap sx={{ maxWidth: 170, color: 'text.secondary' }}>
                            {exp.description || '—'}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.8}>
                          <PersonIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                          <Typography variant="body2" fontWeight={600}>{exp.created_by}</Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        {exp.receipt_file ? (
                          <Tooltip title="View receipt">
                            <IconButton size="small"
                              href={loadPettyExpenses.replace('loadPettyExpenses', 'receipts/') + exp.receipt_file}
                              target="_blank"
                              sx={{ border: '1px solid', borderColor: alpha(PRIMARY, 0.3), borderRadius: 1.5, color: PRIMARY }}>
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Tooltip title={exp.approved_by ? 'Reviewed by: ' + exp.approved_by : 'Awaiting review'} placement="top">
                          <Chip icon={cfg.icon} label={cfg.label} size="small" color={cfg.color}
                            variant={isPending ? 'outlined' : 'filled'}
                            sx={{ fontWeight: 700, fontSize: '0.68rem', height: 26, cursor: 'default' }} />
                        </Tooltip>
                        {exp.approved_by && (
                          <Typography variant="caption" display="block" color="text.disabled"
                            sx={{ fontSize: '0.62rem', mt: 0.3, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {exp.approved_by}
                          </Typography>
                        )}
                      </TableCell>

                      {/* ── Action Buttons (only for pending) ── */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {isPending ? (
                          <Stack direction="row" spacing={0.8}>
                            <Tooltip title={isToday(exp.expense_date) ? "Approve this expense" : "Can only approve same-day expenses"}>
                              <span>
                                <Button size="small" variant="contained"
                                  onClick={function() { handleApprove(exp) }}
                                  disabled={actionLoading || !isToday(exp.expense_date)}
                                  startIcon={<ThumbUpIcon sx={{ fontSize: 13 }} />}
                                  sx={{
                                    fontSize: '0.7rem', px: 1.5, py: 0.6, borderRadius: 2, minWidth: 0,
                                    background: 'linear-gradient(135deg, #0d7a71, #11998e)',
                                    '&:hover': { background: 'linear-gradient(135deg, #0a5e56, #0d7a71)', boxShadow: '0 4px 12px rgba(17,153,142,0.35)' },
                                    '&.Mui-disabled': { opacity: 0.4 }
                                  }}>
                                  Approve
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip title={isToday(exp.expense_date) ? "Reject this expense" : "Can only reject same-day expenses"}>
                              <span>
                                <Button size="small" variant="outlined"
                                  onClick={function() { handleReject(exp) }}
                                  disabled={actionLoading || !isToday(exp.expense_date)}
                                  startIcon={<ThumbDownIcon sx={{ fontSize: 13 }} />}
                                  sx={{
                                    fontSize: '0.7rem', px: 1.5, py: 0.6, borderRadius: 2, minWidth: 0,
                                    color: '#e05252', borderColor: alpha('#e05252', 0.45),
                                    '&:hover': { background: alpha('#e05252', 0.08), borderColor: '#e05252' },
                                    '&.Mui-disabled': { opacity: 0.4 }
                                  }}>
                                  Reject
                                </Button>
                              </span>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>—</Typography>
                        )}
                      </TableCell>

                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Footer summary ── */}
          {filtered.length > 0 && (
            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: alpha(PRIMARY, 0.12), background: alpha(PRIMARY, 0.02) }}>
              <Stack direction="row" spacing={3} flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  Showing <b>{filtered.length}</b> expenses
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total: <b style={{ color: PRIMARY_DARK }}>{GHS} {fmtAmount(filtered.reduce(function(s, e) { return s + parseFloat(e.amount || 0) }, 0))}</b>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Pending: <b style={{ color: '#b37d0a' }}>{filtered.filter(function(e) { return e.status === 'pending' }).length}</b>
                </Typography>
              </Stack>
            </Box>
          )}
        </Paper>

      </Box>

      {/* ── Toast Notifications ── */}
      <NotificationToast notifications={notifications} onDismiss={dismissNotif} />
    </>
  )
}

export default ExpenseApproval